import { useState, useCallback } from 'react';
import type { Flashcard } from '../types';

interface FlashcardFlipProps {
  card: Flashcard;
  onFlip?: (isFlipped: boolean) => void;
  className?: string;
  frontClassName?: string;
  backClassName?: string;
  showHint?: boolean;
  isFlipped?: boolean;
}

export const FlashcardFlip: React.FC<FlashcardFlipProps> = ({
  card,
  onFlip,
  className = '',
  frontClassName = '',
  backClassName = '',
  showHint = true,
  isFlipped: externalFlipped,
}) => {
  const [internalFlipped, setInternalFlipped] = useState(false);
  const [hintVisible, setHintVisible] = useState(false);

  const isControlled = externalFlipped !== undefined;
  const isFlipped = isControlled ? externalFlipped : internalFlipped;

  const handleFlip = useCallback(() => {
    if (isControlled) {
      onFlip?.(!externalFlipped);
    } else {
      setInternalFlipped((prev) => {
        const next = !prev;
        onFlip?.(next);
        return next;
      });
    }
  }, [onFlip, isControlled, externalFlipped]);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === ' ' || e.key === 'Enter') {
        e.preventDefault();
        handleFlip();
      }
    },
    [handleFlip]
  );

  return (
    <div
      className={`relative w-full max-w-md mx-auto cursor-pointer perspective-1000 ${className}`}
      onClick={handleFlip}
      onKeyDown={handleKeyDown}
      role="button"
      tabIndex={0}
      aria-label="Flashcard. Press space or enter to flip."
    >
      <div
        className={`relative w-full transition-transform duration-500 preserve-3d ${
          isFlipped ? 'rotate-y-180' : ''
        }`}
        style={{ transformStyle: 'preserve-3d', minHeight: '240px' }}
      >
        {/* Front */}
        <div
          className={`absolute inset-0 w-full h-full backface-hidden rounded-2xl shadow-xl border-2 border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-6 flex flex-col justify-center items-center text-center hover:shadow-2xl transition-shadow duration-300 ${frontClassName}`}
          style={{ backfaceVisibility: 'hidden' }}
        >
          <div className="space-y-4">
            <span className="inline-block px-3 py-1 rounded-full bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 text-xs font-medium uppercase tracking-wider">
              Question
            </span>
            <h3 className="text-xl font-bold text-gray-900 dark:text-gray-100 leading-relaxed">
              {card.front}
            </h3>
            {showHint && card.hint && !hintVisible && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setHintVisible(true);
                }}
                className="text-sm text-blue-600 dark:text-blue-400 hover:underline underline-offset-2"
              >
                Show Hint
              </button>
            )}
            {showHint && card.hint && hintVisible && (
              <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-3">
                <p className="text-sm text-yellow-800 dark:text-yellow-300 font-medium">
                  💡 {card.hint}
                </p>
              </div>
            )}
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-4">
              Click or press Space to flip
            </p>
          </div>
        </div>

        {/* Back */}
        <div
          className={`absolute inset-0 w-full h-full backface-hidden rounded-2xl shadow-xl border-2 border-blue-200 dark:border-blue-800 bg-gradient-to-br from-blue-50 to-white dark:from-gray-800 dark:to-gray-900 p-6 flex flex-col justify-center items-center text-center hover:shadow-2xl transition-shadow duration-300 ${backClassName}`}
          style={{
            backfaceVisibility: 'hidden',
            transform: 'rotateY(180deg)',
          }}
        >
          <div className="space-y-4">
            <span className="inline-block px-3 py-1 rounded-full bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-300 text-xs font-medium uppercase tracking-wider">
              Answer
            </span>
            <p className="text-lg text-gray-900 dark:text-gray-100 leading-relaxed">
              {card.back}
            </p>
            {card.tags.length > 0 && (
              <div className="flex flex-wrap gap-2 justify-center pt-2">
                {card.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400 text-xs rounded-full"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            )}
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-4">
              Click or press Space to flip back
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FlashcardFlip;
